import { create } from 'zustand'

/**
 * UI state store for modals, toasts, sidebar, loading states, etc.
 */
export const useUIStore = create((set, get) => ({
    // Sidebar state
    sidebarOpen: true,
    setSidebarOpen: (open) => set({ sidebarOpen: open }),
    toggleSidebar: () => set(state => ({ sidebarOpen: !state.sidebarOpen })),

    // Modal state
    modals: {},
    openModal: (modalId, props = {}) =>
        set(state => ({
            modals: { ...state.modals, [modalId]: { isOpen: true, props } }
        })),
    closeModal: (modalId) =>
        set(state => ({
            modals: { ...state.modals, [modalId]: { isOpen: false, props: {} } }
        })),
    isModalOpen: (modalId) => get().modals[modalId]?.isOpen || false,

    // Pricing modal (used by hasFeature)
    showPricingModal: false,
    pricingModalCase: '',
    pricingModalCallback: null,
    // Optional analytics-only context for the current open (e.g. { auto: true } when a gate opened it
    // automatically). null unless the caller passed one; never read for behaviour.
    pricingModalMeta: null,
    hidePricingModalLicenseAssignment: false,
    // When true, the next pricing modal open should force the full
    // (non-minimal) plan list — overrides the Airtable "simplifiedPricing"
    // flag for this open. Reset back to false on close.
    forcePricingModalShowAllPlans: false,
    setShowPricingModal: (show, caseItem = '', callback = null, meta = null) =>
        set({
            showPricingModal: show,
            pricingModalCase: caseItem,
            pricingModalCallback: show ? callback : null,
            pricingModalMeta: meta,
            // Clear the show-all-plans override when the modal closes so a
            // subsequent open from a different caller starts fresh.
            ...(show ? {} : { forcePricingModalShowAllPlans: false }),
        }),
    setHidePricingModalLicenseAssignment: (hide) =>
        set({ hidePricingModalLicenseAssignment: hide }),
    setForcePricingModalShowAllPlans: (force) =>
        set({ forcePricingModalShowAllPlans: !!force }),

    // Guest modal (for users not logged in)
    showGuestModal: false,
    setShowGuestModal: (show) => set({ showGuestModal: show }),

    // Select edit mode modal (for playground users)
    showSelectEditModeModal: false,
    setShowSelectEditModeModal: (show) => set({ showSelectEditModeModal: show }),

    // Publish dialog
    showPublishDialog: false,
    setShowPublishDialog: (show) => set({ showPublishDialog: show }),

    // Toast notifications
    toasts: [],
    addToast: (toast) => {
        const id = Math.random().toString(36).substr(2, 9)
        const newToast = {
            id,
            type: 'info',
            duration: 5000,
            ...toast,
        }
        set(state => ({ toasts: [...state.toasts, newToast] }))

        // Auto remove after duration
        if (newToast.duration > 0) {
            setTimeout(() => {
                get().removeToast(id)
            }, newToast.duration)
        }

        return id
    },
    removeToast: (id) =>
        set(state => ({ toasts: state.toasts.filter(toast => toast.id !== id) })),
    clearToasts: () => set({ toasts: [] }),

    // Global loading states
    loading: {},
    setLoading: (key, isLoading) =>
        set(state => ({
            loading: { ...state.loading, [key]: isLoading }
        })),
    isLoading: (key) => get().loading[key] || false,

    // Filters and search
    filters: {},
    setFilter: (key, value) =>
        set(state => ({
            filters: { ...state.filters, [key]: value }
        })),
    clearFilter: (key) =>
        set(state => {
            const newFilters = { ...state.filters }
            delete newFilters[key]
            return { filters: newFilters }
        }),
    clearAllFilters: () => set({ filters: {} }),

    // Selected items (for bulk operations)
    selections: {},
    setSelection: (key, items) =>
        set(state => ({
            selections: { ...state.selections, [key]: items }
        })),
    clearSelection: (key) =>
        set(state => {
            const newSelections = { ...state.selections }
            delete newSelections[key]
            return { selections: newSelections }
        }),
    toggleItemSelection: (key, itemId) =>
        set(state => {
            const currentSelection = state.selections[key] || []
            const isSelected = currentSelection.includes(itemId)
            const newSelection = isSelected
                ? currentSelection.filter(id => id !== itemId)
                : [...currentSelection, itemId]

            return {
                selections: { ...state.selections, [key]: newSelection }
            }
        }),

    // Sidebar collapsible sections state
    expandedSections: {
        'add-ons': true,
    },

    toggleSection: (sectionId) => set((state) => ({
        expandedSections: {
            // Auto-collapse other sections
            ...Object.keys(state.expandedSections).reduce((acc, key) => {
                acc[key] = key === sectionId ? !state.expandedSections[key] : false
                return acc
            }, {}),
            // Toggle the target section
            [sectionId]: !state.expandedSections[sectionId]
        }
    })),

    isSectionExpanded: (sectionId) => get().expandedSections[sectionId] || false,
}))

// Helper functions for common toast patterns
export const useToast = () => {
    const addToast = useUIStore(state => state.addToast)

    return {
        toast: addToast,
        success: (message, options = {}) => addToast({
            type: 'success',
            title: 'Success',
            message,
            ...options
        }),
        error: (message, options = {}) => addToast({
            type: 'error',
            title: 'Error',
            message,
            duration: 7000,
            ...options
        }),
        warning: (message, options = {}) => addToast({
            type: 'warning',
            title: 'Warning',
            message,
            ...options
        }),
        info: (message, options = {}) => addToast({
            type: 'info',
            title: 'Info',
            message,
            ...options
        }),
    }
}
